import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, Mail, Star, RefreshCw, Send, Check, X,
  Facebook, Instagram, Linkedin, ChevronRight, Filter,
  AlertCircle, Clock, CheckCircle, Loader2, Search, ArrowLeft
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

import { API_URL } from '../../../config/api';

// Google icon
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const platformConfig = {
  facebook: { icon: Facebook, color: 'bg-blue-600', textColor: 'text-blue-600', name: 'Facebook' },
  instagram: { icon: Instagram, color: 'bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500', textColor: 'text-pink-600', name: 'Instagram' },
  linkedin: { icon: Linkedin, color: 'bg-blue-700', textColor: 'text-blue-700', name: 'LinkedIn' },
  google_business: { icon: () => <GoogleIcon className="w-4 h-4" />, color: 'bg-white border border-slate-200', textColor: 'text-slate-700', name: 'Google' }
};

const tabs = [
  { id: 'comments', label: 'Commentaires', icon: MessageCircle },
  { id: 'messages', label: 'Messages', icon: Mail },
  { id: 'reviews', label: 'Avis Google', icon: Star }
];

export default function EngagementPage() {
  const { user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('comments');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, pending
  const [selectedItem, setSelectedItem] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [activeTab, filter]);

  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/marketing/engagement/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchItems = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);

    const endpoint = activeTab === 'comments' ? 'comments' :
                     activeTab === 'messages' ? 'messages' : 'reviews';
    
    let url = `${API_URL}/api/marketing/${endpoint}`;
    if (filter === 'unread') {
      url += '?is_read=false';
    }

    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        let data = await response.json();
        if (filter === 'pending') {
          data = data.filter(item => !item.reply_status);
        }
        setItems(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncItems = async () => {
    const token = localStorage.getItem('client_token');
    setSyncing(true);

    const endpoint = activeTab === 'comments' ? 'comments/sync' :
                     activeTab === 'messages' ? 'messages/sync' : 'reviews/sync';

    try {
      const response = await fetch(`${API_URL}/api/marketing/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchItems();
        fetchStats();
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSyncing(false);
    }
  };

  const markAsRead = async (itemId) => {
    const token = localStorage.getItem('client_token');
    const endpoint = activeTab === 'comments' ? 'comments' :
                     activeTab === 'messages' ? 'messages' : 'reviews';

    try {
      await fetch(`${API_URL}/api/marketing/${endpoint}/${itemId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchItems();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedItem) return;

    const token = localStorage.getItem('client_token');
    setSending(true);

    const endpoint = activeTab === 'comments' ? 'comments' :
                     activeTab === 'messages' ? 'messages' : 'reviews';

    try {
      const response = await fetch(`${API_URL}/api/marketing/${endpoint}/${selectedItem.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyText, use_ai: false })
      });

      if (response.ok) {
        toast.success('Reponse envoyee !');
        setReplyText('');
        setSelectedItem(null);
        fetchItems();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const content = item.content || item.message || '';
    const author = item.author_name || item.sender_name || '';
    return content.toLowerCase().includes(searchQuery.toLowerCase()) ||
           author.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'dd MMM a HH:mm', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="engagement-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/espace-client/marketing">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-rm-text">Engagement</h1>
            <p className="text-sm text-slate-500">Gère tes commentaires, messages et avis</p>
          </div>
        </div>
        <Button onClick={syncItems} disabled={syncing}>
          {syncing ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Synchroniser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-slate-500">Commentaires</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">{stats.unread?.comments || 0}</p>
            <p className="text-xs text-slate-400">non lus</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-slate-500">Messages</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">{stats.unread?.messages || 0}</p>
            <p className="text-xs text-slate-400">non lus</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-amber-500" />
              <span className="text-sm text-slate-500">Avis Google</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">{stats.unread?.reviews || 0}</p>
            <p className="text-xs text-slate-400">non lus</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-slate-500">A repondre</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">
              {(stats.pending_replies?.comments || 0) + (stats.pending_replies?.reviews || 0)}
            </p>
            <p className="text-xs text-slate-400">en attente</p>
          </div>
        </div>
      )}

      {/* Tabs & Filters */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex gap-2">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const count = stats?.unread?.[tab.id] || 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-rm-primary text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.id ? 'bg-white/20' : 'bg-red-500 text-white'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-48 focus:ring-2 focus:ring-rm-primary focus:border-transparent"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="all">Tous</option>
              <option value="unread">Non lus</option>
              <option value="pending">A repondre</option>
            </select>
          </div>
        </div>

        {/* Items List */}
        <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-rm-primary" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-slate-400 mb-2">
                {activeTab === 'comments' ? <MessageCircle className="w-12 h-12 mx-auto" /> :
                 activeTab === 'messages' ? <Mail className="w-12 h-12 mx-auto" /> :
                 <Star className="w-12 h-12 mx-auto" />}
              </div>
              <p className="text-slate-500">Aucun element a afficher</p>
              <p className="text-sm text-slate-400 mt-1">
                Cliquez sur "Synchroniser" pour recuperer les dernieres donnees
              </p>
            </div>
          ) : (
            filteredItems.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                type={activeTab}
                onClick={() => {
                  setSelectedItem(item);
                  if (!item.is_read) markAsRead(item.id);
                }}
                isSelected={selectedItem?.id === item.id}
                formatDate={formatDate}
              />
            ))
          )}
        </div>
      </div>

      {/* Reply Panel */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
          >
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-3">
                {(() => {
                  const platform = platformConfig[selectedItem.platform] || {};
                  const Icon = platform.icon || MessageCircle;
                  return (
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${platform.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                  );
                })()}
                <div>
                  <p className="font-medium text-rm-text">
                    {selectedItem.author_name || selectedItem.sender_name || 'Anonyme'}
                  </p>
                  <p className="text-xs text-slate-500">{formatDate(selectedItem.created_at)}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedItem(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-4">
              {/* Original message */}
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                {activeTab === 'reviews' && selectedItem.rating && (
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(star => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= (selectedItem.rating === 'FIVE' ? 5 :
                                   selectedItem.rating === 'FOUR' ? 4 :
                                   selectedItem.rating === 'THREE' ? 3 :
                                   selectedItem.rating === 'TWO' ? 2 : 1)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-slate-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
                <p className="text-slate-700">{selectedItem.content || selectedItem.message}</p>
                {selectedItem.post_content && (
                  <p className="text-xs text-slate-400 mt-2">
                    En reponse a: "{selectedItem.post_content}..."
                  </p>
                )}
              </div>
              
              {/* Previous reply if exists */}
              {selectedItem.reply_content && (
                <div className="bg-green-50 rounded-lg p-3 mb-4 border-l-4 border-green-500">
                  <p className="text-xs text-green-600 mb-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Reponse envoyee
                  </p>
                  <p className="text-slate-700">{selectedItem.reply_content}</p>
                </div>
              )}
              
              {/* Reply input */}
              {!selectedItem.reply_status && (
                <div className="flex gap-3">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Écris ta réponse..."
                    rows={2}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg resize-none focus:ring-2 focus:ring-rm-primary focus:border-transparent"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className="self-end"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Item Row Component
function ItemRow({ item, type, onClick, isSelected, formatDate }) {
  const platform = platformConfig[item.platform] || {};
  const Icon = platform.icon || MessageCircle;
  
  return (
    <div
      onClick={onClick}
      className={`p-4 cursor-pointer transition-colors ${
        isSelected ? 'bg-rm-primary/5' : 'hover:bg-slate-50'
      } ${!item.is_read ? 'bg-blue-50/50' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${platform.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-rm-text">
                {item.author_name || item.sender_name || 'Anonyme'}
              </span>
              {!item.is_read && (
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
              {type === 'reviews' && item.rating && (
                <div className="flex items-center">
                  {[1,2,3,4,5].map(star => (
                    <Star
                      key={star}
                      className={`w-3 h-3 ${
                        star <= (item.rating === 'FIVE' ? 5 :
                                 item.rating === 'FOUR' ? 4 :
                                 item.rating === 'THREE' ? 3 :
                                 item.rating === 'TWO' ? 2 : 1)
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-slate-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
          </div>
          
          <p className="text-sm text-slate-600 line-clamp-2">
            {item.content || item.message}
          </p>
          
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${platform.textColor} bg-slate-100`}>
              {platform.name}
            </span>
            {item.reply_status === 'sent' && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Repondu
              </span>
            )}
            {item.reply_status === 'failed' && (
              <span className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Echec
              </span>
            )}
            {!item.reply_status && item.is_read && (
              <span className="text-xs text-orange-600 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                A repondre
              </span>
            )}
          </div>
        </div>
        
        <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
      </div>
    </div>
  );
}
